import type { ChatLogEntry, ThreadItemData, TurnGroup } from '../types';

// 20-color palette for Task delegations
export const TASK_COLORS = [
  '#E57373', '#F06292', '#BA68C8', '#9575CD', '#7986CB',
  '#64B5F6', '#4FC3F7', '#4DD0E1', '#4DB6AC', '#81C784',
  '#AED581', '#DCE775', '#FFF176', '#FFD54F', '#FFB74D',
  '#FF8A65', '#A1887F', '#90A4AE', '#78909C', '#B0BEC5'
];

/**
 * Transform a batch of ChatLogEntry items to ThreadItemData for display
 */
export const transformLogsToThreadItems = (logs: ChatLogEntry[]): ThreadItemData[] => {
  const threads: ThreadItemData[] = [];
  if (!logs) return threads;

  let taskColorIndex = 0;
  const processedToolCalls = new Set<string>();

  logs.forEach((log, index) => {
    // User Request
    if (log.kind === 'request' || log.kind === 'prompt') {
      if (log.toolCallId) return;

      const content = log.prompt || '';
      if (!content) return;

      threads.push({
        id: log.id || `user-${index}`,
        type: 'user',
        content,
        turn: log.turn,
        isHistorical: log.isHistorical,
      });
      return;
    }

    // AI Response
    if (log.kind === 'response') {
      if (!log.response && log.status !== 'streaming') return;

      threads.push({
        id: log.id || `ai-${index}`,
        type: 'ai-response',
        status: log.status === 'streaming' ? 'active' : 'completed',
        content: log.response || '',
        turn: log.turn,
        isHistorical: log.isHistorical,
      });
      return;
    }

    // Tool Calls
    if (log.kind === 'tool_call' && log.toolCallId) {
      if (processedToolCalls.has(log.toolCallId)) return;
      processedToolCalls.add(log.toolCallId);

      const isTask = log.tool?.toLowerCase() === 'task';
      const isComplete = log.status === 'complete';
      const hasError = log.status === 'error';

      if (isTask) {
        let title = 'Task';
        try {
          const args = typeof log.args === 'string' ? JSON.parse(log.args) : log.args || {};
          title = (args.description as string) || `${args.subagent_type || 'Agent'}: Working...`;
        } catch {
          title = 'Task';
        }

        const taskColor = TASK_COLORS[taskColorIndex % TASK_COLORS.length];
        taskColorIndex++;

        threads.push({
          id: log.id || `task-${index}`,
          type: 'task',
          status: hasError ? 'error' : (isComplete ? 'completed' : 'active'),
          title,
          taskColor,
          turn: log.turn,
          isHistorical: log.isHistorical,
        });
      } else {
        if (!isComplete) {
          threads.push({
            id: log.id || `working-${index}`,
            type: 'working',
            status: hasError ? 'error' : 'active',
            title: 'Working...',
            turn: log.turn,
            isHistorical: log.isHistorical,
          });
        }
      }
    }
  });

  return threads;
};

/**
 * Group ThreadItemData by turn into TurnGroup array
 */
export const groupByTurn = (items: ThreadItemData[], isStreaming: boolean): TurnGroup[] => {
  const turnMap = new Map<number, ThreadItemData[]>();

  items.forEach(item => {
    const turn = item.turn ?? 0;
    if (!turnMap.has(turn)) {
      turnMap.set(turn, []);
    }
    turnMap.get(turn)!.push(item);
  });

  const groups: TurnGroup[] = [];
  const sortedTurns = Array.from(turnMap.keys()).sort((a, b) => a - b);

  sortedTurns.forEach((turn, turnIndex) => {
    const turnItems = turnMap.get(turn)!;
    const isLastTurn = turnIndex === sortedTurns.length - 1;

    if (turnItems.length === 0) return;

    // Find first user message
    const firstUserIndex = turnItems.findIndex(item => item.type === 'user');
    const firstMessage = firstUserIndex >= 0 ? turnItems[firstUserIndex] : null;

    // Determine if this turn is historical
    // A turn is historical only if ALL items in it are marked as historical
    const isHistoricalTurn = turnItems.every(item => item.isHistorical === true);

    // If only one item in turn, no thinking section needed
    if (turnItems.length === 1) {
      groups.push({
        turn,
        firstMessage: turnItems[0],
        thinkingMessages: [],
        lastMessage: null,
        isStreaming: isLastTurn && isStreaming,
        isHistorical: isHistoricalTurn,
      });
      return;
    }

    // Last message is the final item in the turn
    const lastMessage = turnItems[turnItems.length - 1];

    // Thinking messages are everything between first and last (excluding user messages and last)
    const thinkingMessages: ThreadItemData[] = [];
    turnItems.forEach((item, idx) => {
      // Skip if it's the first user message
      if (firstUserIndex >= 0 && idx === firstUserIndex) return;
      // Skip if it's the last message
      if (idx === turnItems.length - 1) return;
      // Skip empty content
      if (item.content?.match(/^\n+$/)) return;

      thinkingMessages.push(item);
    });

    groups.push({
      turn,
      firstMessage,
      thinkingMessages,
      lastMessage: lastMessage !== firstMessage ? lastMessage : null,
      isStreaming: isLastTurn && isStreaming,
      isHistorical: isHistoricalTurn,
    });
  });

  return groups;
};

/**
 * Create a new TurnGroup from a user message (for incremental updates)
 */
export const createTurnGroupFromUserMessage = (
  userItem: ThreadItemData,
  turn: number
): TurnGroup => {
  return {
    turn,
    firstMessage: userItem,
    thinkingMessages: [],
    lastMessage: null,
    isStreaming: true,
    isHistorical: false,
  };
};

